import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { Button } from "@/components/ui/button";
import {
  lineUnitPrice,
  reasonLabel,
  type ReorderLine
} from "@/lib/reorder-diff";
import type { GroupedConfirmLines } from "@/lib/voice-order";
import { convertToLocale } from "@/utils/money";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { AlertTriangle, Search, ShoppingCart, Store } from "lucide-react";

interface SellerReviewStageProps {
  grouped: GroupedConfirmLines;
  currencyCode: string;
  isAdding: boolean;
  error: string | null;
  onSearch: (query: string) => void;
  onBack: () => void;
  onConfirm: () => void;
}

/**
 * Stage 2 (seller-grouped final review): the live confirm `dry_run` diff,
 * grouped client-side by resolved seller. Surfaces repriced and unavailable
 * lines inline (never a silent stale add), and states plainly when one spoken
 * order will fan out into several supplier carts. Confirming re-calls confirm
 * with `dry_run: false` to commit into those carts.
 */
export function SellerReviewStage({
  grouped,
  currencyCode,
  isAdding,
  error,
  onSearch,
  onBack,
  onConfirm
}: SellerReviewStageProps) {
  const { sellerGroups, unavailableLines, sellerCount } = grouped;
  const fmt = (amount: number) =>
    convertToLocale({ amount, currency_code: currencyCode });
  const addableCount = sellerGroups.reduce(
    (total, group) => total + group.lines.length,
    0
  );

  return (
    <>
      <div className="max-h-[52vh] overflow-y-auto px-6 py-2">
        {sellerCount > 1 ? (
          <p className="mt-2 rounded-md bg-purple-50 px-3 py-2 text-xs text-purple-700 dark:bg-purple-900/20 dark:text-purple-300">
            This order will be split into {sellerCount} carts, one per supplier
            - each supplier ships and is paid separately.
          </p>
        ) : null}

        {sellerGroups.map((group) => {
          const subtotal = group.lines.reduce(
            (sum, line) => sum + lineUnitPrice(line) * line.quantity,
            0
          );
          return (
            <section key={group.seller.id} className="mt-4">
              <div className="mb-1 flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-1.5">
                  <Store
                    className="h-3.5 w-3.5 flex-shrink-0 text-gray-400"
                    aria-hidden="true"
                  />
                  <TruncateTooltip
                    as="h3"
                    className="text-sm font-semibold text-gray-900 dark:text-white"
                  >
                    {group.seller.name || "Supplier"}
                  </TruncateTooltip>
                </div>
                <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                  {fmt(subtotal)}
                </span>
              </div>
              <ul className="divide-border divide-y">
                {group.lines.map((line, index) => (
                  <SellerLineRow
                    key={line.offer_id ?? line.variant_id ?? `line-${index}`}
                    line={line}
                    fmt={fmt}
                  />
                ))}
              </ul>
            </section>
          );
        })}

        {unavailableLines.length > 0 ? (
          <section className="mt-4">
            <h3 className="mb-1 text-sm font-semibold text-gray-500 dark:text-gray-400">
              Couldn't add ({unavailableLines.length})
            </h3>
            <ul className="divide-border divide-y">
              {unavailableLines.map((line, index) => (
                <UnavailableLineRow
                  key={line.variant_id ?? `unavailable-${index}`}
                  line={line}
                  onSearch={onSearch}
                />
              ))}
            </ul>
          </section>
        ) : null}

        {addableCount === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            None of these items are available from a supplier right now.
          </p>
        ) : null}
      </div>

      {error ? (
        <p
          role="alert"
          className="mx-6 mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
        >
          {error}
        </p>
      ) : null}

      <footer className="border-border flex flex-col-reverse gap-2 border-t p-6 pt-4 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          disabled={addableCount === 0 || isAdding}
        >
          <ShoppingCart className="mr-2 h-4 w-4" aria-hidden="true" />
          {isAdding
            ? "Adding…"
            : `Confirm & add ${addableCount} ${addableCount === 1 ? "item" : "items"}`}
        </Button>
      </footer>
    </>
  );
}

function SellerLineRow({
  line,
  fmt
}: {
  line: ReorderLine;
  fmt: (amount: number) => string;
}) {
  return (
    <li className="flex gap-3 py-3">
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-neutral-800">
        <ProductThumbnailImage
          src={line.thumbnail ?? null}
          alt=""
          className="h-full w-full object-cover"
          fallback={null}
          enablePreview={false}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <TruncateTooltip
            as="p"
            className="text-sm font-medium text-gray-900 dark:text-white"
          >
            {line.title}
          </TruncateTooltip>
          <LinePrice line={line} fmt={fmt} />
        </div>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Qty: {line.quantity}
        </p>
        {line.status === "repriced" ? (
          <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            Price updated to the current live price
          </p>
        ) : null}
      </div>
    </li>
  );
}

function LinePrice({
  line,
  fmt
}: {
  line: ReorderLine;
  fmt: (amount: number) => string;
}) {
  if (line.status === "repriced") {
    return (
      <span className="flex-shrink-0 text-right text-sm">
        <span className="mr-1 text-gray-400 line-through">
          {fmt(line.old_price ?? 0)}
        </span>
        <span className="font-semibold text-gray-900 dark:text-white">
          {fmt(line.new_price ?? 0)}
        </span>
      </span>
    );
  }
  return (
    <span className="flex-shrink-0 text-right text-sm font-semibold text-gray-900 dark:text-white">
      {fmt(line.unit_price ?? 0)}
    </span>
  );
}

function UnavailableLineRow({
  line,
  onSearch
}: {
  line: ReorderLine;
  onSearch: (query: string) => void;
}) {
  return (
    <li className="flex items-start justify-between gap-3 py-3">
      <div className="flex min-w-0 gap-3">
        <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-neutral-800">
          <ProductThumbnailImage
            src={line.thumbnail ?? null}
            alt=""
            className="h-full w-full object-cover opacity-50 grayscale"
            fallback={null}
            enablePreview={false}
          />
        </div>
        <div className="min-w-0">
          <TruncateTooltip
            as="p"
            className="text-sm font-medium text-gray-400 line-through dark:text-gray-500"
          >
            {line.title}
          </TruncateTooltip>
          <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-neutral-800 dark:text-gray-400">
            {reasonLabel(line.reason)}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onSearch(line.title)}
        className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium text-purple-600 hover:underline dark:text-purple-400"
      >
        <Search className="h-3 w-3" aria-hidden="true" />
        Find alternative
      </button>
    </li>
  );
}
